/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  Button,
  Divider,
  Flex,
  Link,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import { useToggle } from '@tonic-one/react-hooks';
import CSpinner from '../../../components/CSpinner';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { Steps } from 'antd';
import {
  DataSynced,
  DataSyncFailed,
  DataSyncing,
  EnablePolicyRadioGroup,
  ProvisionOrgInfo,
  StepIcon,
} from './share';
import { Trans, useTranslation } from 'react-i18next';
import { deleteCookie, getProvisionCookie } from '@Utils/common';
import {
  EnabledPolicyTypeEnum,
  IdSyncStatus,
  MultiAppsAuthenticationInfoRes,
  ProvisionModalState,
  ProvisionStatusEnum,
  RejectProvisionReason,
  TokenStatus,
} from '@Types/provision';
import { ProvisionApi } from '@Apis';
import { reportPendo } from '@Utils/setupPendo';
import ErrorAlert from '../../../components/Common/ErrorAlert';
import RejectProvisionModal from './rejectProvisionModal';
import { CAS_DOC_LINK, getCasOLHLink } from '@Utils/casDocLink';
import i18next from 'i18next';
import { App, Service } from '@Types/app';

//后台传来的是 做完的状态
enum TeamsGraphProvisionStatus {
  NotProvision = 0,
  ProvisioningGrantStep1Done = 1,
  ProvisioningGrantStep2Done = 2, //这个状态就是ProvisioningSyncing了
  Provisioned = 3,
}

const SERVICE = Service.Teams;

const TeamsGraphModal = ({
  orgId,
  onClose,
  isOpen,
  provisionStatus,
}: ProvisionModalState) => {
  const { t } = useTranslation('provision');
  const [errorMsg, setErrorMsg] = useState<ReactNode | undefined>(undefined);
  const getInitStatus = () => {
    if (provisionStatus.status === ProvisionStatusEnum.NotStarted) {
      return TeamsGraphProvisionStatus.NotProvision;
    } else if (provisionStatus.status === ProvisionStatusEnum.Provisioned) {
      return TeamsGraphProvisionStatus.Provisioned;
    } else {
      const { apps: appInfo } = provisionStatus?.authorization as {
        apps: {
          app: string;
          status: string;
        }[];
      };
      // first app is teamsApp, second app is teamsBasicApp
      const [teamsApp, teamsBasicApp] = appInfo;
      if (provisionStatus.status === ProvisionStatusEnum.Provisioning) {
        //默认第一个app肯定成功了，只要确认第二个app是否已经provision
        //判断是 ProvisioningGrantStep1Done 还是 ProvisioningGrantStep2Done
        if (
          teamsApp.status === TokenStatus.NotStarted ||
          teamsApp.status === TokenStatus.Created
        ) {
          return TeamsGraphProvisionStatus.ProvisioningGrantStep1Done;
        } else {
          //默认是TokenStatus.Created
          return TeamsGraphProvisionStatus.ProvisioningGrantStep2Done;
        }
      } else {
        const statusArr = [
          TokenStatus.NotStarted,
          TokenStatus.Failure,
        ] as string[];
        //check which step has done fail
        if (statusArr.includes(teamsApp.status)) {
          //first permission not success, set to not provision
          return TeamsGraphProvisionStatus.NotProvision;
        } else if (statusArr.includes(teamsBasicApp.status)) {
          //second permission not success, set to step 1 done
          return TeamsGraphProvisionStatus.ProvisioningGrantStep1Done;
        } else {
          //sync data fail
          return TeamsGraphProvisionStatus.ProvisioningGrantStep2Done;
        }
      }
    }
  };

  const [syncDataFailFlag, setSyncDataFailFlag] = useState(
    provisionStatus.status === ProvisionStatusEnum.Failure &&
      [IdSyncStatus.Failure, 'FAILURE'].includes(
        provisionStatus?.idSync?.status
      )
  );
  const [teamsStatus, setTeamsStatus] =
    useState<TeamsGraphProvisionStatus>(getInitStatus());

  //example:
  // [
  //   {
  //     app: 'teams',
  //     installAppUrl: null,
  //     authenticationUrl:
  //       'https://login.windows.net/common/oauth2/authorize?client_id=f362d789-5a05-4faa-9708-70933c56e3ab&response_type=code&prompt=consent&resource=https://graph.microsoft.com&state=azuread_mail_inline_graph_api_98829450685893498823c6ebec592aba&redirect_uri=https%3A%2F%2Fadmin-dev.tmcas.trendmicro.net%2Fprovision.html',
  //   },
  //   {
  //     app: 'teamsBasic',
  //     installAppUrl: null,
  //     authenticationUrl:
  //       'https://login.windows.net/common/oauth2/authorize?client_id=8af0038e-67da-438d-9424-ce0e1e5f6325&response_type=code&prompt=consent&resource=https://graph.microsoft.com&state=azuread_mail_inline_graph_api_98829450685893498823c6ebec592aba&redirect_uri=https%3A%2F%2Fadmin-dev.tmcas.trendmicro.net%2Fprovision.html',
  //   },
  // ];
  const [authenticationInfo, setAuthenticationInfo] =
    useState<null | MultiAppsAuthenticationInfoRes>(null);

  const [rejectReason, setRejectReason] =
    useState<RejectProvisionReason | null>(null);
  useEffect(() => {
    if (
      [
        TeamsGraphProvisionStatus.NotProvision,
        TeamsGraphProvisionStatus.ProvisioningGrantStep1Done,
      ].includes(teamsStatus)
    ) {
      ProvisionApi.apiGetMultiAppsAuthenticationInfos(Service.Teams, orgId)
        .then((res) => {
          setAuthenticationInfo(res.data);
        })
        .catch((e) => {
          const errorCode = e.data?.error?.details?.[0].errorCode;
          if (
            errorCode === RejectProvisionReason.ServiceConflict ||
            errorCode === RejectProvisionReason.PocConflict
          ) {
            setRejectReason(errorCode);
          }
        });
    }
  }, []);

  if (rejectReason) {
    return (
      <RejectProvisionModal
        onClose={onClose}
        service={SERVICE}
        reason={rejectReason}
      />
    );
  }

  //防止在加载数据的时候会闪烁
  if (
    [
      TeamsGraphProvisionStatus.NotProvision,
      TeamsGraphProvisionStatus.ProvisioningGrantStep1Done,
    ].includes(teamsStatus) &&
    !authenticationInfo
  ) {
    return <></>;
  }

  return (
    <>
      <Modal isOpen={isOpen} isClosable onClose={onClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('provision.service.teams.title')}</ModalHeader>

          <ModalBody>
            <ProvisionOrgInfo orgId={orgId} />

            {errorMsg && (
              <ErrorAlert
                errorMsg={errorMsg}
                onClose={() => setErrorMsg(undefined)}
              />
            )}
            <GrantPermissionSteps
              {...{
                teamsStatus,
                setTeamsStatus,
                authenticationInfo,
                orgId,
                setErrorMsg,
                // appId: provisionStatus?.authorization?.apps?.[0].appId || '',
              }}
            />
            {teamsStatus ===
              TeamsGraphProvisionStatus.ProvisioningGrantStep2Done &&
              (syncDataFailFlag ? (
                <DataSyncFailed
                  service={SERVICE}
                  onRetry={async () => {
                    const res = await ProvisionApi.apiRetrySyncData(
                      SERVICE,
                      orgId
                    );
                    if (res.data?.status === 'FAILURE') {
                      if ([813, 814].includes(res.data?.error)) {
                        setErrorMsg(
                          <Trans
                            i18nKey={`provision:provision.service.teams.error.saveInfo.FAILURE;${res.data?.error}`}
                          >
                            placeholder
                            <Link
                              href={getCasOLHLink(
                                CAS_DOC_LINK.provision.teams.userConfiguration,
                                i18next.language
                              )}
                              target="_blank"
                            />
                            placeholder
                          </Trans>
                        );
                      } else {
                        setErrorMsg(
                          t([
                            `provision.service.teams.error.saveInfo.${res.data?.reason}`,
                            'common.apiException.internalError',
                          ])
                        );
                      }
                    } else {
                      setErrorMsg(undefined);
                      setSyncDataFailFlag(false);
                    }
                  }}
                />
              ) : (
                <DataSyncing
                  onRefresh={async () => {
                    const res = await ProvisionApi.apiGetProvisionStatus(
                      SERVICE,
                      orgId
                    );
                    if (res.data.status === ProvisionStatusEnum.Provisioned) {
                      setTeamsStatus(TeamsGraphProvisionStatus.Provisioned);
                    }
                  }}
                />
              ))}
            {teamsStatus === TeamsGraphProvisionStatus.Provisioned && (
              <DataSynced />
            )}
          </ModalBody>
          <ModalFooter>
            <Button minWidth="20x" onClick={onClose}>
              {t('common.button.close')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default TeamsGraphModal;

//eslint-disable-next-line @typescript-eslint/no-unused-vars
const GrantPermissionSteps = ({
  teamsStatus,
  setTeamsStatus,
  authenticationInfo,
  orgId,
  setErrorMsg,
}: {
  teamsStatus: TeamsGraphProvisionStatus;
  setTeamsStatus: (status: TeamsGraphProvisionStatus) => void;
  authenticationInfo: MultiAppsAuthenticationInfoRes | null;
  orgId: string;
  setErrorMsg: (msg: string | undefined) => void;
}) => {
  const { t } = useTranslation('provision');
  const [loading, toggleLoading] = useToggle(false);
  const timeRef = useRef<any>(null);
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const titleTextStyle = {
    fontSize: '16px',
    lineHeight: '22px',
    fontWeight: '400',
    color: colorStyle.color.primary,
  };
  const radioGroupRef = useRef<{
    enabledPolicyType: EnabledPolicyTypeEnum;
  }>({
    enabledPolicyType: EnabledPolicyTypeEnum.Default,
  });

  const onFirstPermission = () => {
    const authUrl = authenticationInfo?.[0]?.authenticationUrl;
    if (!authUrl) {
      return;
    }
    setErrorMsg(undefined);
    reportPendo('Provision.GrantPermission.Click', {
      orgId,
      service: Service.Teams,
    });
    toggleLoading(true);
    window.open(authUrl, '_blank');
    if (timeRef.current) {
      clearInterval(timeRef.current);
    }
    timeRef.current = setInterval(async () => {
      const teamsProvisionInfo = getProvisionCookie(
        'provision_status_azuread_graph_teams'
      );
      if (teamsProvisionInfo) {
        clearInterval(timeRef.current);
        deleteCookie('provision_status_');
        //{"code": "123"}
        const { error, code } = JSON.parse(teamsProvisionInfo);
        if (error === undefined || error.length === 0) {
          try {
            const res = await ProvisionApi.apiSaveProvisionInfo(
              App.Teams,
              code,
              radioGroupRef.current.enabledPolicyType,
              orgId
            );
            if (res.data?.status === 'failure') {
              // failure data = {
              //     "status": "failure",
              //     "reason": "FAILURE;451"  |  "PROVISION;CONFLICT"
              // }
              setErrorMsg(
                t([
                  `provision.service.teams.error.saveInfo.${res.data?.reason}`,
                  'common.apiException.internalError',
                ])
              );
            } else {
              const statusRes = await ProvisionApi.apiGetProvisionStatus(
                Service.Teams,
                orgId
              );
              const teamsTokenStatus =
                statusRes.data?.authorization?.apps?.[0]?.status;
              if (teamsTokenStatus === TokenStatus.Created) {
                setTeamsStatus(
                  TeamsGraphProvisionStatus.ProvisioningGrantStep1Done
                );
              } else {
                setErrorMsg(
                  t('provision.service.teams.error.saveInfo.FAILURE;450')
                );
              }
            }
          } catch {
            setErrorMsg(t('common.apiException.internalError'));
          }
        } else {
          setErrorMsg(t('provision.service.teams.error.deniedGrant'));
        }
        toggleLoading(false);
      }
    }, 1000);
  };
  const onSecondPermission = () => {
    const authUrl = authenticationInfo?.[1]?.authenticationUrl;
    if (!authUrl) {
      return;
    }
    setErrorMsg(undefined);
    reportPendo('Provision.GrantPermission.Click', {
      orgId,
      service: App.TeamsBasic,
    });
    toggleLoading(true);
    window.open(authUrl, '_blank');
    if (timeRef.current) {
      clearInterval(timeRef.current);
    }
    timeRef.current = setInterval(async () => {
      const teamsBasicProvisionInfo = getProvisionCookie(
        'provision_status_azuread_graph_basic_teams'
      );
      if (teamsBasicProvisionInfo) {
        clearInterval(timeRef.current);
        deleteCookie('provision_status_');
        const { error, code } = JSON.parse(teamsBasicProvisionInfo);
        if (error === undefined || error.length === 0) {
          try {
            const res = await ProvisionApi.apiSaveProvisionInfo(
              App.TeamsBasic,
              code,
              EnabledPolicyTypeEnum.None,
              orgId
            );
            if (res.data?.status === 'failure') {
              // failure data = {
              //         "status": "failure",
              //         "reason": "FAILURE;451"
              //     }
              setErrorMsg(
                t([
                  `provision.service.teams.error.saveInfo.${res.data?.reason}`,
                  'common.apiException.internalError',
                ])
              );
            } else {
              setTeamsStatus(
                TeamsGraphProvisionStatus.ProvisioningGrantStep2Done
              );
            }
          } catch {
            setErrorMsg(t('common.apiException.internalError'));
          }
        } else {
          setErrorMsg(t('provision.service.teams.error.deniedGrant'));
        }
        toggleLoading(false);
      }
    }, 1000);
  };

  useEffect(() => {
    if (timeRef.current) {
      clearInterval(timeRef.current);
    }
    return () => {
      if (timeRef.current) {
        clearInterval(timeRef.current);
      }
    };
  }, []);

  return (
    <>
      {loading && <CSpinner />}
      <Steps
        direction="vertical"
        current={teamsStatus - 1}
        items={[
          {
            title: (
              <Text {...titleTextStyle}>
                <Trans
                  i18nKey={
                    'provision.service.teams.details.userConfiguration.STEP1'
                  }
                  ns="provision"
                >
                  placeholder
                  <Link
                    href={getCasOLHLink(
                      CAS_DOC_LINK.provision.teams.userConfiguration,
                      i18next.language
                    )}
                    target="_blank"
                  >
                    placeholder
                  </Link>
                  placeholder
                </Trans>
              </Text>
            ),
            description: (
              <Flex direction="column" pt="16px" gap="16px">
                {teamsStatus <
                TeamsGraphProvisionStatus.ProvisioningGrantStep1Done ? (
                  <>
                    <Divider variant="dashed" orientation="horizontal" />
                    <Text color={colorStyle.color.secondary}>
                      {/* TODO: i18n "Select the policy to enable when the access grant is completed:" */}
                      {t('provision.common.selectPolicyToEnable')}
                    </Text>
                    <EnablePolicyRadioGroup
                      service={Service.Teams}
                      ref={radioGroupRef}
                    />
                    <Divider variant="dashed" orientation="horizontal" />
                    <Box>
                      <Button
                        variant="secondary"
                        borderRadius="2rem"
                        onClick={() => {
                          onFirstPermission();
                        }}
                      >
                        {t('provision.button.grantPermission')}
                      </Button>
                    </Box>
                  </>
                ) : (
                  <Box>
                    <Text color={colorStyle.color.tertiary}>
                      {t('provision.common.permissionGranted')}
                    </Text>
                  </Box>
                )}
              </Flex>
            ),
            icon: (
              <StepIcon
                checked={
                  teamsStatus >=
                  TeamsGraphProvisionStatus.ProvisioningGrantStep1Done
                }
              />
            ),
          },
          {
            title: (
              <Text color={colorStyle.color.primary}>
                {t('provision.service.teams.details.userConfiguration.STEP2')}
              </Text>
            ),
            description: (
              <Flex direction="column" pt="16px" gap="16px">
                <Box>
                  {teamsStatus <
                  TeamsGraphProvisionStatus.ProvisioningGrantStep2Done ? (
                    <Button
                      variant="secondary"
                      borderRadius="2rem"
                      disabled={
                        teamsStatus !==
                        TeamsGraphProvisionStatus.ProvisioningGrantStep1Done
                      }
                      onClick={() => {
                        onSecondPermission();
                      }}
                    >
                      {t('provision.button.grantPermission')}
                    </Button>
                  ) : (
                    <Text color={colorStyle.color.tertiary}>
                      {t('provision.common.permissionGranted')}
                    </Text>
                  )}
                </Box>
              </Flex>
            ),
            icon: (
              <StepIcon
                checked={
                  teamsStatus >=
                  TeamsGraphProvisionStatus.ProvisioningGrantStep2Done
                }
              />
            ),
          },
        ]}
      />
    </>
  );
};
